const LITRE_SYMBOL = '\u2113';

function coeff(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function parseInputValue(value) {
    if (value == null || value === '') {
        return null;
    }
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
}

/**
 * Forward calibration fit: ℓ = a₂x² + a₁x + a₀ (x = %, y = ℓ).
 * Used when level signal unit is PERCENTAGE.
 */
export function convertPercentageToLiters(x, a0, a1, a2) {
    const pct = parseInputValue(x);
    if (pct == null) {
        return null;
    }
    const c0 = coeff(a0);
    const c1 = coeff(a1);
    const c2 = coeff(a2);
    // Fuel Ledger clamps mapped litres at zero after applying the polynomial.
    return Math.max(0, (c2 * (pct ** 2)) + (c1 * pct) + c0);
}

/**
 * Inverse: solve ℓ = a₂x² + a₁x + a₀ for x (%) given y (ℓ).
 * Used when level signal unit is LITRE.
 */
export function convertLitersToPercentage(y, a0, a1, a2) {
    const litres = parseInputValue(y);
    if (litres == null) {
        return null;
    }
    const c0 = coeff(a0);
    const c1 = coeff(a1);
    const c2 = coeff(a2);

    if (c2 === 0) {
        if (c1 === 0) {
            return null;
        }
        return (litres - c0) / c1;
    }

    const c = c0 - litres;
    const discriminant = (c1 * c1) - (4 * c2 * c);
    if (discriminant < 0) {
        return null;
    }

    const sqrtD = Math.sqrt(discriminant);
    const sign = c1 >= 0 ? 1 : -1;
    const q = -0.5 * (c1 + (sign * sqrtD));
    if (q === 0) {
        return 0;
    }
    const root1 = q / c2;
    const root2 = c / q;

    let dist1 = Math.min(Math.abs(root1 - 0), Math.abs(root1 - 100));
    if (root1 >= 0 && root1 <= 100) {
        dist1 = 0;
    }

    let dist2 = Math.min(Math.abs(root2 - 0), Math.abs(root2 - 100));
    if (root2 >= 0 && root2 <= 100) {
        dist2 = 0;
    }

    return (dist1 < dist2) ? root1 : root2;
}

export function equationCoeffsFromMapping(equationMap) {
    const mapping = equationMap?.mapping || {};
    return {
        a2: coeff(mapping.a2 ?? mapping.A2),
        a1: coeff(mapping.a1 ?? mapping.A1),
        a0: coeff(mapping.a0 ?? mapping.A0),
    };
}

/** Primary equation label for the fuel map equation block. */
export function fuelMapEquationFormulaForFormTelemetry(telemetryInput) {
    const l = LITRE_SYMBOL;
    if (telemetryInput === 'voltage') {
        return `${l} = a₂V² + a₁V + a₀`;
    }
    return `${l} = a₂%² + a₁% + a₀`;
}

/** Inverse formula lines shown when level signal unit is Litre. */
export const FUEL_MAP_EQUATION_INVERSE_FORMULAS_FOR_LITRE = {
    linear: `% = (${LITRE_SYMBOL} - a₀) / a₁`,
    polynomial: `% = (-a₁ ± √(a₁² - 4a₂(a₀ - ${LITRE_SYMBOL}))) / 2a₂`,
};
